import { cn } from "@/lib/utils";

// Peças compartilhadas fora das listagens: atalho de acessibilidade, botões de
// ícone, menu de perfil, navegação estrutural, estados de página e paginação.

export const skipLinkClass =
  "fixed left-2 top-2 z-[1000] -translate-y-[160%] rounded-md bg-[var(--orange)] px-4 py-[0.65rem] font-bold text-white focus:translate-y-0";

// `icon-button` fica no className: o aviso de operação concluída pinta o botão
// de fechar pela cor dele (`.operation-feedback .icon-button`).
export const iconButtonClass =
  cn(
  // TUT-282 — h-[2.45rem]/w-[2.45rem] eram um dos seis valores soltos; agora
  // lê do token padrão (--control-h, 2.5rem).
  "icon-button inline-flex h-[var(--control-h)] w-[var(--control-h)] cursor-pointer items-center justify-center rounded-lg border-0 bg-transparent",
  "transition-[background,color,border-color,transform] duration-[var(--transition)]",
  "hover:bg-[var(--surface-3)] hover:text-[var(--primary)]",
);

export const profileMenuClass = cn(
  // TUT-192 — os três controles do header dividem a MESMA régua: altura
  // --control-h (a do `iconButtonClass`), raio e hover iguais. O avatar
  // continua 2.35rem por dentro (decorativo, não é o alvo de toque); o que
  // muda é a área de clique, que antes era só o conteúdo e desalinhava com
  // os vizinhos.
  "flex h-[var(--control-h)] cursor-pointer items-center gap-[0.35rem] rounded-lg border-0 bg-transparent px-[0.15rem] text-[var(--text)]",
  "transition-[background,color] duration-[var(--transition)] hover:bg-[var(--surface-3)]",
  // Avatar com iniciais: paleta fixa, não acompanha o tema. TUT-510 (revisão
  // 1): a paleta fixa é a da MARCA (`--brand-*`), não hex solto (P34).
  "[&>span]:grid [&>span]:size-[2.35rem] [&>span]:place-items-center [&>span]:rounded-full [&>span]:border [&>span]:border-[var(--brand-border)] [&>span]:bg-[var(--brand-blue)] [&>span]:font-semibold [&>span]:text-white",
  "[&_svg:not([class*='size-'])]:size-[0.9rem]",
);

export const profilePopoverClass = cn(
  "grid w-[210px] gap-[0.15rem] border-[var(--border)] bg-[var(--surface)] p-[0.8rem]",
  "[&_small]:text-[var(--muted)]",
  "[&_button]:mt-[0.55rem] [&_button]:cursor-pointer [&_button]:rounded-md [&_button]:border [&_button]:border-[var(--border)] [&_button]:bg-[var(--surface-2)] [&_button]:p-2 [&_button]:text-left [&_button]:text-[var(--text)]",
);

export const profilePopoverLinkClass =
  "mt-[0.55rem] flex items-center gap-[0.45rem] rounded-md border border-[var(--border)] bg-[var(--surface-2)] p-2 text-[var(--text)] no-underline [&_svg:not([class*='size-'])]:size-4";

// Botão que abre e fecha o drawer. Só existe abaixo de 980px, onde a navegação
// deixa de ser fixa na lateral.
//
// É autossuficiente de propósito, em vez de compor com `iconButtonClass`:
// aquele traz `border-0`, e como as duas utilitárias de largura de borda têm a
// mesma especificidade, quem vence é a ordem de emissão do Tailwind — que
// zerava a borda de 1px deste botão. Repetir as poucas declarações aqui é mais
// barato que depender dessa ordem.
//
// Com o drawer ABERTO ele atravessa para a borda direita do painel: fechado
// mora no canto da tela, mas ali o painel encosta a marca no mesmo ponto e um
// tapava o outro.
export const rowLinkClass =
  "grid h-[1.8rem] w-[1.8rem] place-items-center rounded-[5px] hover:bg-[var(--primary-highlight)] hover:text-[var(--primary)] [&_svg:not([class*='size-'])]:size-[0.95rem]";

// Em tela estreita a trilha some e dá lugar ao "voltar".
export const breadcrumbClass =
  "mb-[0.7rem] flex items-center gap-[0.55rem] text-legenda text-[var(--muted)] max-[720px]:hidden [&_a]:text-[var(--primary)]";

export const backLinkClass =
  "mb-2 hidden items-center gap-[0.3rem] text-legenda text-[var(--primary)] max-[720px]:flex [&_svg:not([class*='size-'])]:size-[0.9rem]";

export const pageStateClass =
  "grid min-h-[60vh] content-center place-items-center gap-[0.7rem] text-[var(--muted)] [&>svg:not([class*='size-'])]:size-8 [&>svg]:text-[var(--primary)] [&_p]:m-0";

export const paginationClass = cn(
  "mt-[0.85rem] flex flex-wrap items-center justify-between gap-3 max-[640px]:justify-center",
  // TUT-469 — dentro do painel de vidro (canto de 24px), a linha encostava o
  // "1–3 de 3" e o "Próxima" na curva do canto; ali ela ganha respiro.
  "in-data-[slot=panel]:px-[0.9rem] in-data-[slot=panel]:pb-[0.9rem]",
  "[&_.button[disabled]]:cursor-not-allowed [&_.button[disabled]]:opacity-45",
);

export const paginationStatusClass =
  "m-0 text-legenda text-[var(--muted)] [&_strong]:text-[var(--text)]";

// `flex-wrap` + `justify-center`: a 320px os dois botões e o "página X de Y"
// não cabem numa linha, e o pai (`paginationClass`) já quebra — mas quebra
// de pai não encolhe FILHO maior que a viewport, e era esta linha o filho.
//
// Passou a não caber em 05/08, quando o contorno de 1px das variantes do
// `<Button>` voltou a existir: os botões estavam sendo desenhados sem borda
// por causa de uma regra fora de layer no `tailwind.css`, e a paginação vivia
// no limite exato do piso de 320px sem que ninguém soubesse. A guarda de
// reflow pegou na primeira execução depois do conserto — que é o trabalho
// dela, e a razão de o piso ser testado e não estimado.
export const paginationControlsClass =
  "flex flex-wrap items-center justify-center gap-[0.6rem]";

export const paginationPageClass =
  "whitespace-nowrap text-legenda text-[var(--muted)] [&_strong]:text-[var(--text)]";
